// API Error Handling Utilities

interface ApiCacheEntry<T> {
    data: T;
    timestamp: number;
}

class ApiCache {
    private cache: Map<string, ApiCacheEntry<any>> = new Map();

    get<T>(key: string, ttlMs: number): T | null {
        const entry = this.cache.get(key);
        if (!entry) return null;

        const age = Date.now() - entry.timestamp;
        if (age > ttlMs) {
            this.cache.delete(key);
            return null;
        }

        return entry.data as T;
    }

    set<T>(key: string, data: T): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
        });
    }

    clear(): void {
        this.cache.clear();
    }
}

export const apiCache = new ApiCache();

export async function fetchWithRetry<T>(
    url: string,
    options: {
        retries?: number;
        retryDelay?: number;
        timeout?: number;
    } = {}
): Promise<T> {
    const {
        retries = 3,
        retryDelay = 1000,
        timeout = 10000,
    } = options;

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            // Create abort controller for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            const response = await fetch(url, {
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return response.json() as Promise<T>;
        } catch (error) {
            lastError = error as Error;

            // Don't retry on abort (timeout) or 4xx errors
            if (
                error instanceof Error &&
                (error.name === 'AbortError' ||
                    (error.message.includes('HTTP 4')))
            ) {
                throw error;
            }

            // Wait before retrying (exponential backoff)
            if (attempt < retries) {
                await new Promise(resolve =>
                    setTimeout(resolve, retryDelay * Math.pow(2, attempt))
                );
            }
        }
    }

    throw lastError || new Error('Fetch failed after retries');
}

export function isOnline(): boolean {
    return navigator.onLine;
}

export function getErrorMessage(error: unknown): string {
    if (!isOnline()) {
        return 'No internet connection. Please check your network and try again.';
    }

    if (error instanceof Error) {
        if (error.name === 'AbortError') {
            return 'Request timed out. The server took too long to respond.';
        }
        if (error.message.includes('HTTP 5')) {
            return 'Server error. Please try again later.';
        }
        if (error.message.includes('HTTP 4')) {
            return 'Request failed. The data may be unavailable.';
        }
        return error.message;
    }

    return 'An unexpected error occurred. Please try again.';
}
